import { useTranslation } from 'react-i18next';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  toast,
} from '@kognitos/lattice';
import { DeleteAutomationTriggerStage } from '@/generated/hooks/grimoire/models';
import { useDeleteAutomationTrigger } from '@/generated/hooks/grimoire/v1/automation_phase';
import { captureException } from '@/modules/sentry';
import { errorHandler } from '@/shared/utils/error-handler';
import {
  TRIGGER_TYPE,
  type TTriggerType,
} from '../../utils/trigger-status-utils';

/**
 * Props for the DeleteTriggerDialog component.
 */
export interface IDeleteTriggerDialogProps {
  /** Whether the dialog is open */
  isOpen: boolean;
  /** Callback to close the dialog */
  onClose: () => void;
  /** The type of trigger being deleted */
  triggerType: TTriggerType;
  /** The organization ID */
  organizationId: string;
  /** The workspace ID */
  workspaceId: string;
  /** The automation ID */
  automationId: string;
  /** The trigger ID to delete */
  triggerId: string;
  /** Whether the automation is a draft */
  isDraft?: boolean;
  /** Callback when delete is successful */
  onSuccess?: () => void;
}

/**
 * A destructive confirmation dialog for deleting triggers.
 *
 * Displays trigger-type-specific messaging (webhook vs email) and handles the
 * delete mutation with loading states, toast notifications, and error handling.
 * Deleting a trigger tears down its external (BDK) instance and cannot be undone.
 *
 * @param {IDeleteTriggerDialogProps} props - Component props
 * @returns {JSX.Element} The delete trigger confirmation dialog
 *
 * @example
 * <DeleteTriggerDialog
 *   isOpen={isDeleteDialogOpen}
 *   onClose={closeDeleteDialog}
 *   triggerType="email"
 *   organizationId={orgId}
 *   workspaceId={wsId}
 *   automationId={autoId}
 *   triggerId={triggerId}
 *   onSuccess={handleDeleteSuccess}
 * />
 */
export function DeleteTriggerDialog({
  isOpen,
  onClose,
  triggerType,
  organizationId,
  workspaceId,
  automationId,
  triggerId,
  isDraft = false,
  onSuccess,
}: IDeleteTriggerDialogProps) {
  const { t } = useTranslation('automation-inputs');

  const { mutate: deleteTrigger, isPending } = useDeleteAutomationTrigger({
    mutation: {
      onSuccess: () => {
        toast.success(t('trigger.toast.deleteSuccess'));
        onClose();
        onSuccess?.();
      },
      onError: error => {
        // errorHandler auto-shows the access-denied toast for 403s; for other
        // errors, fall back to the generic delete-error toast + Sentry capture.
        errorHandler(error, () => {
          toast.error(t('trigger.toast.deleteError'));
          captureException(error, {
            tags: {
              component: 'DeleteTriggerDialog',
              action: 'deleteTrigger',
            },
          });
        });
      },
    },
  });

  /**
   * Handles the delete trigger action when the user confirms.
   */
  const handleDeleteTrigger = () => {
    deleteTrigger({
      organization: organizationId,
      workspace: workspaceId,
      automation: automationId,
      triggerId,
      params: {
        stage: isDraft
          ? DeleteAutomationTriggerStage.AUTOMATION_STAGE_DRAFT
          : DeleteAutomationTriggerStage.AUTOMATION_STAGE_PUBLISHED,
      },
    });
  };

  const isWebhook = triggerType === TRIGGER_TYPE.WEBHOOK;

  return (
    <AlertDialog open={isOpen} onOpenChange={open => !open && onClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {isWebhook
              ? t('deleteDialog.webhookTitle')
              : t('deleteDialog.emailTitle')}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {isWebhook
              ? t('deleteDialog.webhookDescription')
              : t('deleteDialog.emailDescription')}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="gap-2">
          <AlertDialogCancel
            type="button"
            onClick={onClose}
            disabled={isPending}
          >
            {t('deleteDialog.cancel')}
          </AlertDialogCancel>
          <AlertDialogAction asChild variant="destructive">
            <Button
              type="button"
              onClick={handleDeleteTrigger}
              disabled={isPending}
              isLoading={isPending}
            >
              {t('deleteDialog.deleteTrigger')}
            </Button>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
